/**
 * The coach-mark component `.mc-coach` (ui-ux §12.4): an ink scrim with a cut-out around the target (8 px padding,
 * 8 px radius), a pulsing gold ring on the target (1.2 s; static when reduced), and a speech-scroll bubble ≤ 80 chars
 * placed in the thumb half when possible. Mandatory marks block other input; the others close on a tap anywhere.
 * Opened through the OverlayManager at priority 80. Owner: UI/Visual (F3).
 */
import { h } from '../dom';
import { overlays, type OverlayManager } from '../overlay';
import { placeFloating } from '../kit/place';
import { ut } from '../strings';

export interface CoachMarkOpts { id: string; target: Element | DOMRect; text: string; mandatory?: boolean; onClose?: () => void; manager?: OverlayManager }

export function showCoachMark(o: CoachMarkOpts): () => void {
  const mgr = o.manager ?? overlays;
  const rect = o.target instanceof Element ? o.target.getBoundingClientRect() : o.target;
  const pad = 8, rad = 8;
  const W = innerWidth, H = innerHeight;
  const cut = { x: rect.left - pad, y: rect.top - pad, w: rect.width + 2 * pad, h: rect.height + 2 * pad };
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('class', 'mc-coach__scrim');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  const path = document.createElementNS(ns, 'path');
  const rr = `M${cut.x + rad} ${cut.y}H${cut.x + cut.w - rad}A${rad} ${rad} 0 0 1 ${cut.x + cut.w} ${cut.y + rad}V${cut.y + cut.h - rad}A${rad} ${rad} 0 0 1 ${cut.x + cut.w - rad} ${cut.y + cut.h}H${cut.x + rad}A${rad} ${rad} 0 0 1 ${cut.x} ${cut.y + cut.h - rad}V${cut.y + rad}A${rad} ${rad} 0 0 1 ${cut.x + rad} ${cut.y}Z`;
  path.setAttribute('d', `M0 0H${W}V${H}H0Z${rr}`);
  svg.appendChild(path);
  const ring = h('div', { class: 'mc-coach__ring', style: { left: `${cut.x}px`, top: `${cut.y}px`, width: `${cut.w}px`, height: `${cut.h}px` } });
  const bubble = h('div', { class: 'mc-coach__bubble' },
    h('div', { class: 'mc-scroll', attrs: { role: 'note' } }, h('span', { class: 'mc-scroll__text' }, o.text)),
    o.mandatory ? null : h('span', { class: 'mc-coach__hint' }, ut('ui.coach.tapAnywhere')));
  const el = h('div', { class: 'mc-coach', attrs: { popover: 'manual', 'data-coach': o.id, 'aria-live': 'polite' } }, svg, ring, bubble);
  let closed = false;
  const close = (): void => { if (closed) return; closed = true; mgr.close(`coach-${o.id}`); el.remove(); o.onClose?.(); };
  if (!o.mandatory) el.addEventListener('pointerdown', (e) => { e.preventDefault(); close(); });
  else {
    // mandatory: the painted scrim swallows input; the cut-out passes it through to the target
    el.style.pointerEvents = 'none';
    svg.style.pointerEvents = 'none';
    path.style.pointerEvents = 'visiblePainted';
    path.addEventListener('pointerdown', (e) => e.preventDefault());
  }
  mgr.show({ id: `coach-${o.id}`, kind: 'coach', el, modal: false, onRequestClose: () => (o.mandatory ? false : (close(), undefined)) });
  // the bubble goes to the thumb half when the target is in the top half, else above the target
  const bw = Math.min(300, W - 32);
  bubble.style.width = `${bw}px`;
  const bh = bubble.offsetHeight || 60;
  const prefer = rect.top + rect.height / 2 < H * 0.55 ? 'bottom' : 'top';
  const p = placeFloating({ x: cut.x, y: cut.y, w: cut.w, h: cut.h }, { w: bw, h: bh }, { w: W, h: H }, { prefer, gap: 14, pad: 16 });
  bubble.style.left = `${p.x}px`;
  bubble.style.top = `${p.y}px`;
  return close;
}
